import React, { useState } from 'react';
import { Layout, Menu, Breadcrumb, Button, theme } from 'antd';
import { ExclamationCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import type { MenuProps } from 'antd';

const { Header, Content, Sider } = Layout;

interface DesktopLayoutProps {
  children: React.ReactNode;
  navItems: MenuProps['items'];
  siderMenus: Record<string, MenuProps['items']>;
  selectedNav: string;
  selectedSide: string[];
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({ 
  children, 
  navItems,
  siderMenus, 
  selectedNav,
  selectedSide 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bannerVisible, setBannerVisible] = useState(true);
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 计算顶部横幅高度
  const bannerHeight = bannerVisible ? 48 : 0;

  return (
    <Layout style={{ height: '100vh', background: 'transparent' }}>
      {/* 桌面端保持原有的全局开发提示横幅 */}
      {bannerVisible && (
        <div
          style={{
            background: 'linear-gradient(135deg, #ff6b6b, #ee5a24, #ff9ff3, #54a0ff)',
            backgroundSize: '400% 400%',
            animation: 'bannerGradient 8s ease infinite',
            color: '#fff',
            padding: '10px 0',
            textAlign: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1001,
            borderBottom: '2px solid rgba(255,255,255,0.2)',
          }}
        >
          <div style={{ 
            maxWidth: 'var(--container-lg)',
            margin: '0 auto',
            padding: '0 var(--spacing-3xl) 0 var(--spacing-md)',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ExclamationCircleOutlined style={{ 
              fontSize: '16px',
              animation: 'bannerPulse 2s ease-in-out infinite'
            }} />
            <span style={{
              fontWeight: '600',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}>
              开发测试阶段 · 功能仅供参考 · 不承担任何责任
            </span>
            <span style={{
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: 'rgba(255,255,255,0.2)',
              padding: '3px 10px',
              borderRadius: '14px',
              border: '1px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🤖 <span style={{ letterSpacing: '0.3px' }}>99% Built with AI</span>
            </span>
            <span style={{ 
              fontSize: '12px', 
              opacity: 0.9,
              fontStyle: 'italic'
            }}>
              风险自担 | Use at your own risk
            </span>
          </div>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => setBannerVisible(false)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              padding: '0 6px',
              minWidth: 'auto',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              opacity: 0.9,
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
            title="关闭提示"
          />
        </div>
      )}

      {/* 桌面端顶部导航 - 固定定位 */}
      <Header 
        style={{ 
          position: 'fixed',
          top: bannerHeight,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}
      >
        <div className="demo-logo" style={{ color: '#fff', fontWeight: 'bold', fontSize: 20 }}>
          DDDD Tools
        </div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[selectedNav]}
          items={navItems}
          style={{ flex: 1, minWidth: 0, marginLeft: 24 }}
          onClick={({ key }) => {
            if (key === 'chain') navigate('/chain');
            else if (key === 'query') navigate('/query');
            else if (key === 'monitor') navigate('/monitor');
            else if (key === 'cex') navigate('/cex');
            else if (key === 'gas') navigate('/gas');
            else if (key === 'tools') navigate('/tools');
          }}
        />
        <div style={{ marginLeft: 24 }}>
          <ConnectButton />
        </div>
      </Header>

      {/* 桌面端主体布局 - 适配固定头部后的高度 */}
      <Layout style={{ background: 'transparent' }}>
        <Sider 
          width={200} 
          style={{ 
            background: colorBgContainer,
            position: 'fixed',
            left: 0,
            top: bannerHeight + 64,
            bottom: 0,
            zIndex: 999,
            overflowY: 'auto'
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedSide}
            style={{ height: '100%', borderRight: 0 }}
            items={siderMenus[selectedNav] || []}
            onClick={({ key }) => navigate(key as string)}
          />
        </Sider>
        
        <Layout style={{ marginLeft: 200, marginTop: bannerHeight + 64, height: `calc(100vh - ${bannerHeight + 64}px)`, background: '#f0f2f5' }}>
          <div 
            className="content-scroll"
            style={{ 
              height: '100%', 
              overflowY: 'auto',
              padding: '0 var(--spacing-lg) var(--spacing-lg)'
            }}
          >
            <Breadcrumb
              items={[
                { title: '首页' }, 
                { title: (navItems?.find(i => i && typeof i === 'object' && 'label' in i && i.key === selectedNav) as { label?: string })?.label }
              ]}
              style={{ margin: 'var(--spacing-md) 0' }}
            />
            <Content
              style={{
                padding: 24,
                margin: 0,
                minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              {children}
            </Content>
          </div>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default DesktopLayout; 